import { useState } from "react";

import type { CreatedAdministrator } from "../api/types";
import { ErrorNotice } from "../components/ui";
import { ConfirmDialog } from "../products/ConfirmDialog";
import { useCreateAdministrator } from "./useUsers";

/**
 * Create an administrator.
 *
 * THERE IS NO ROLE CHOOSER, deliberately. A new account starts as a Viewer
 * because least privilege is the contract, and giving it more is a separate
 * act — separately confirmed, separately audited, and visible in the account's
 * history as its own event rather than buried in its creation.
 *
 * THERE IS NO PASSWORD FIELD EITHER. The server generates one, so nobody has to
 * invent a password for somebody else and no password chosen in a browser is
 * ever sent. It comes back exactly once.
 *
 * The owner's own password confirms the change. It is typed here, sent in this
 * one request, and kept nowhere.
 */
export function CreateAdministratorDialog({
  onCreated,
  onClose,
}: {
  onCreated: (created: CreatedAdministrator) => void;
  onClose: () => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const create = useCreateAdministrator();

  const ready =
    displayName.trim().length > 0 &&
    email.trim().length > 0 &&
    currentPassword.length > 0;

  return (
    <ConfirmDialog
      title="Create administrator"
      confirmLabel="Create administrator"
      busy={create.isPending}
      confirmDisabled={!ready}
      onCancel={onClose}
      onConfirm={() => {
        if (!ready) return;
        create.mutate(
          {
            display_name: displayName.trim(),
            email: email.trim(),
            current_password: currentPassword,
          },
          { onSuccess: onCreated },
        );
      }}
    >
      <div className="stack" data-testid="create-administrator-dialog">
        <div className="users__field">
          <label className="users__label" htmlFor="new-display-name">
            Display name
          </label>
          <input
            id="new-display-name"
            type="text"
            autoFocus
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>

        <div className="users__field">
          <label className="users__label" htmlFor="new-email">
            Email
          </label>
          <input
            id="new-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <p className="muted">
          The new administrator starts as a <strong>Viewer</strong> and can read
          the catalogue and orders. Give them more from the Roles dialog once
          they exist.
        </p>

        <div className="users__field">
          <label className="users__label" htmlFor="create-confirm-password">
            Your password
          </label>
          <input
            id="create-confirm-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
          <p className="users__hint">
            Your own password, to confirm this change. It is not the new
            administrator&apos;s — the server generates that one and shows it to
            you once.
          </p>
        </div>

        {create.error ? <ErrorNotice error={create.error} /> : null}
      </div>
    </ConfirmDialog>
  );
}
